import {
  IconActivity,
  IconBox,
  IconBuildingCastle,
  IconCloudNetwork,
  IconKey,
  IconLayoutGrid,
  IconMailBolt,
  IconRadar2,
  IconShieldHalf,
  IconWall,
} from "@tabler/icons-react";
import React from "react";
import { JSX } from "react";

import { ControlCategory, ControlStatus } from "@oneleet/api_client";
import { cn } from "@oneleet/utils";

import {
  CircleApp,
  CircleCfr21part11,
  CircleCis,
  CircleDora,
  CircleDpf,
  CircleGdpr,
  CircleHipaa,
  CircleIso27001,
  CircleNist,
  CircleOneleet,
  CirclePci,
  CircleSoc2,
} from "@oneleet/components/src/icons";

const iconClassName = "text-neutral-550 size-[1.125rem] shrink-0";

const categoryToIcon: Record<ControlCategory, React.ReactElement> = {
  [ControlCategory.ACCESS_CONTROL_AND_AUTHORIZATION]: (
    <IconKey className={iconClassName} />
  ),
  [ControlCategory.INFRASTRUCTURE_SECURITY]: (
    <IconCloudNetwork className={iconClassName} />
  ),
  [ControlCategory.VULNERABILITY_MANAGEMENT]: (
    <IconShieldHalf className={iconClassName} />
  ),
  [ControlCategory.DISASTER_RECOVERY]: <IconWall className={iconClassName} />,
  [ControlCategory.MONITORING_AND_INCIDENT_RESPONSE]: (
    <IconActivity className={iconClassName} />
  ),
  [ControlCategory.ORGANIZATIONAL_SECURITY]: (
    <IconBuildingCastle className={iconClassName} />
  ),
  [ControlCategory.ENDPOINT_SECURITY]: <IconBox className={iconClassName} />,
  [ControlCategory.DATA_MANAGEMENT_AND_PROTECTION]: (
    <IconLayoutGrid className={iconClassName} />
  ),
  [ControlCategory.RISK_MANAGEMENT]: <IconRadar2 className={iconClassName} />,
  [ControlCategory.EMAIL_SECURITY]: <IconMailBolt className={iconClassName} />,
};

const DefaultIcon = <IconBox className={iconClassName} />;

export const getIconForCategory = (
  category: ControlCategory,
  className?: string,
) => {
  const icon = Object.hasOwn(categoryToIcon, category)
    ? categoryToIcon[category]
    : DefaultIcon;

  if (!className) return icon;

  return React.cloneElement(icon, {
    className: cn(iconClassName, className),
  });
};

export const statusOrder: Record<ControlStatus, number> = {
  [ControlStatus.PASSING]: 1,
  [ControlStatus.FAILING]: 2,
  [ControlStatus.NEEDS_CHANGES]: 3,
  [ControlStatus.IN_REVIEW]: 4,
  [ControlStatus.IN_PROGRESS]: 5,
  [ControlStatus.NOT_STARTED]: 6,
};

const iconMapping: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  cisIG1_v1: CircleCis,
  soc2_v1: CircleSoc2,
  soc2_privacy_v1: CircleSoc2,
  soc2_availability_v1: CircleSoc2,
  soc2_confidentiality_v1: CircleSoc2,
  soc2_integrity_v1: CircleSoc2,
  hipaa_v1: CircleHipaa,
  iso27001_v1: CircleIso27001,
  pciDssLevel1_v1: CirclePci,
  gdpr_v1: CircleGdpr,
  euDora_v1: CircleDora,
  nist800171_v1: CircleNist,
  hipaa_business_associate_v1: CircleHipaa,
  hipaa_business_associate_no_phi_v1: CircleHipaa,
  hipaa_health_plan_v1: CircleHipaa,
  hipaa_clearinghouse_v1: CircleHipaa,
  hipaa_provider_v1: CircleHipaa,
  cfr21part11_v1: CircleCfr21part11,
  oneleetEssentials_v1: CircleOneleet,
  oneleetPro_v1: CircleOneleet,

  // Frameworks with no content currently, purely for trust center display purposes
  eu_us_dpf_v1: CircleDpf,
  uk_us_dpg_v1: CircleDpf,
  swiss_us_dpf_v1: CircleDpf,
  app_v1: CircleApp,
};

export const frameworkToCircleIcon = (
  id: string,
  className: string,
): JSX.Element | null => {
  const IconComponent = iconMapping[id];
  if (!IconComponent) {
    return null;
  }
  return <IconComponent className={className} />;
};
